import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

export default class VideoProductSlider extends Component {
  #index = 0;
  #resizeObserver = null;
  #videoObserver = null;
  #abortController = null;
  #swipeAbort = null;
  #cachedProductGrids = new Map();
  #modalProductId = null;
  #modalProductUrl = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initVideoPosters();
    this.#initLazyVideos();
    this.#resizeObserver = new ResizeObserver(() => this.#update());
    this.#resizeObserver.observe(this);
    this.#setupSwipe();
    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#videoObserver?.disconnect();
    this.#abortController?.abort();
    this.#swipeAbort?.abort();
    this.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  #setupSwipe() {
    const viewport = this.querySelector('.vpc-slider__viewport');
    if (!viewport) return;

    viewport.style.touchAction = 'pan-y';

    this.#swipeAbort = new AbortController();
    const { signal } = this.#swipeAbort;

    let startX = 0;
    let startY = 0;
    let active = false;
    let didSwipe = false;
    const THRESHOLD = 40;

    viewport.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'touch') return;
      startX = e.clientX;
      startY = e.clientY;
      active = true;
      didSwipe = false;
    }, { signal, passive: true });

    viewport.addEventListener('pointermove', (e) => {
      if (!active || e.pointerType !== 'touch') return;
      if (Math.abs(e.clientX - startX) > 8) didSwipe = true;
    }, { signal, passive: true });

    viewport.addEventListener('pointerup', (e) => {
      if (!active || e.pointerType !== 'touch') return;
      active = false;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (Math.abs(dx) < THRESHOLD) return;
      if (Math.abs(dy) > Math.abs(dx)) return;
      if (dx < 0) this.next();
      else this.prev();
    }, { signal, passive: true });

    viewport.addEventListener('pointercancel', () => {
      active = false;
    }, { signal, passive: true });

    viewport.addEventListener('click', (e) => {
      if (didSwipe) {
        e.preventDefault();
        e.stopPropagation();
        didSwipe = false;
      }
    }, { signal, capture: true });
  }

  prev() {
    this.#go(this.#index - 1);
  }

  next() {
    this.#go(this.#index + 1);
  }

  async openModal(event) {
    event.preventDefault();

    const trigger = event.target.closest('.vpc-card__qv-trigger');
    if (!(trigger instanceof HTMLButtonElement)) return;

    const productUrl = trigger.dataset.productUrl;
    if (!productUrl) return;

    const productGrid = await this.#getProductGrid(productUrl);
    if (!productGrid) return;

    this.#renderModal(productGrid, productUrl);
    this.refs.dialog?.showModal();
  }

  closeModal(event) {
    event?.preventDefault();

    for (const nestedDialog of this.refs.modalDetails?.querySelectorAll('dialog[open]') ?? []) {
      nestedDialog.close();
    }

    this.refs.dialog?.close();
    this.#modalProductId = null;
    this.#modalProductUrl = null;
  }

  handleDialogClick(event) {
    if (event.target === this.refs.dialog) {
      const surface = this.refs.dialog?.querySelector('.vpc-modal__surface');
      if (!(surface instanceof HTMLElement) || !(event instanceof MouseEvent)) {
        this.closeModal();
        return;
      }

      const { left, right, top, bottom } = surface.getBoundingClientRect();
      const clickedBackdrop =
        event.clientX < left ||
        event.clientX > right ||
        event.clientY < top ||
        event.clientY > bottom;

      if (clickedBackdrop) {
        this.closeModal();
      }
      return;
    }

    const guideTrigger = event.target.closest('.custom-buy-area__link--guide');
    if (guideTrigger && this.refs.modalDetails?.contains(guideTrigger)) {
      event.preventDefault();
      const dialog = guideTrigger.closest('.custom-buy-area')?.querySelector('.custom-buy-area__dialog');
      if (dialog && !dialog.open) {
        dialog.show();
      }
      return;
    }

    const nestedClose = event.target.closest('.custom-buy-area__dialog-close');
    if (nestedClose && this.refs.modalDetails?.contains(nestedClose)) {
      event.preventDefault();
      nestedClose.closest('.custom-buy-area__dialog')?.close();
      return;
    }

    if (
      event.target instanceof HTMLDialogElement &&
      event.target.classList.contains('custom-buy-area__dialog') &&
      this.refs.modalDetails?.contains(event.target)
    ) {
      event.target.close();
    }
  }

  #handleCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;
    if (!this.refs.dialog?.open) return;
    if (!this.contains(event.target)) return;
    this.closeModal();
  };

  #handleVariantUpdate = (event) => {
    if (!this.refs.dialog?.open) return;
    if (!this.refs.modalDetails?.contains(event.target)) return;

    const updatedGrid = event.detail?.data?.html?.querySelector?.('[data-product-grid-content]');
    if (!updatedGrid) return;

    const nextProductId = event.detail?.data?.newProduct?.id || event.detail?.data?.productId;
    if (this.#modalProductId && nextProductId && String(this.#modalProductId) !== String(nextProductId)) {
      this.#modalProductId = nextProductId;
    }

    const nextProductUrl = event.detail?.data?.newProduct?.url || this.#modalProductUrl;

    requestAnimationFrame(() => {
      this.#renderModal(/** @type {Element} */ (updatedGrid.cloneNode(true)), nextProductUrl);
    });
  };

  #getColumns() {
    return parseInt(getComputedStyle(this).getPropertyValue('--vpc-active-columns')) || 4;
  }

  #go(index) {
    const slides = this.refs.slides ?? [];
    const cols = this.#getColumns();
    const max = Math.max(0, slides.length - cols);
    this.#index = Math.min(Math.max(0, index), max);
    this.#update();
  }

  #getGap() {
    return parseFloat(getComputedStyle(this).getPropertyValue('--vpc-gap')) || 0;
  }

  #update() {
    const slides = this.refs.slides ?? [];
    const cols = this.#getColumns();
    const max = Math.max(0, slides.length - cols);
    const centered = slides.length <= cols;

    if (centered) {
      this.setAttribute('data-centered', '');
      this.#index = 0;
      this.refs.track?.style.setProperty('--vpc-offset', '0px');
    } else {
      this.removeAttribute('data-centered');
      const slideWidth = slides[0]?.offsetWidth ?? 0;
      const gap = this.#getGap();
      this.refs.track?.style.setProperty('--vpc-offset', `${-this.#index * (slideWidth + gap)}px`);
    }

    if (this.refs.btnPrev) this.refs.btnPrev.disabled = centered || this.#index <= 0;
    if (this.refs.btnNext) this.refs.btnNext.disabled = centered || this.#index >= max;
  }

  async #getProductGrid(productUrl) {
    const cacheKey = new URL(productUrl, window.location.origin).toString();
    const cached = this.#cachedProductGrids.get(cacheKey);
    if (cached) {
      return /** @type {Element} */ (cached.cloneNode(true));
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productUrl, { signal: this.#abortController.signal });
      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const productGrid = html.querySelector('[data-product-grid-content]');
      if (!productGrid) {
        throw new Error('No product grid content found for video product slider modal');
      }

      this.#cachedProductGrids.set(cacheKey, /** @type {Element} */ (productGrid.cloneNode(true)));
      return /** @type {Element} */ (productGrid.cloneNode(true));
    } catch (error) {
      if (error.name === 'AbortError') return null;
      console.error(error);
      return null;
    } finally {
      this.#abortController = null;
    }
  }

  #renderModal(productGrid, productUrl) {
    const productDetails = productGrid.querySelector('.product-details');
    if (!productDetails) return;

    this.#modalProductUrl = productUrl;
    this.#modalProductId =
      productDetails.querySelector('product-form-component')?.dataset.productId ||
      productDetails.querySelector('variant-picker')?.dataset.productId ||
      this.#modalProductId;

    const title = productDetails.querySelector('.view-product-title')?.cloneNode(true);
    const price = productDetails.querySelector('product-price.custom-product-price, product-price')?.cloneNode(true);
    const colorPicker = productDetails.querySelector('.custom-variant-color')?.cloneNode(true);
    const buyArea = productDetails.querySelector('.custom-buy-area')?.cloneNode(true);

    if (title) {
      const titleLink = title.querySelector('a');
      if (titleLink) {
        titleLink.href = productUrl;
      }
    }

    if (buyArea) {
      buyArea.querySelector('.custom-buy-area__sticky-bar')?.remove();
    }

    for (const node of [colorPicker, buyArea]) {
      if (!(node instanceof Element)) continue;
      this.#sanitizeModalNode(node);
    }

    const detailsInner = document.createElement('div');
    detailsInner.className = 'vpc-modal__details-inner';
    detailsInner.replaceChildren(...[title, price, colorPicker, buyArea].filter(Boolean));

    this.refs.modalDetails?.replaceChildren(detailsInner);
    this.#patchModalVariantPickers();
    this.#patchSizePickers();

    const mediaImages = [...productGrid.querySelectorAll('slideshow-slide .product-media img')];
    this.refs.modalMedia?.replaceChildren(this.#buildMediaCarousel(mediaImages));
  }

  #sanitizeModalNode(node) {
    for (const script of node.querySelectorAll('script')) {
      script.remove();
    }

    for (const picker of node.querySelectorAll('variant-picker')) {
      picker.removeAttribute('data-template-product-match');
    }
  }

  #patchSizePickers() {
    for (const picker of this.refs.modalDetails?.querySelectorAll('variant-picker[data-picker-role="size"]') ?? []) {
      if (picker.dataset.vpcPatched === 'true') continue;
      if (typeof picker.updateVariantPicker !== 'function') continue;

      const originalUpdate = picker.updateVariantPicker.bind(picker);
      picker.updateVariantPicker = function (newHtml) {
        const scopedHtml = /** @type {Document | Element} */ (
          newHtml.cloneNode ? newHtml.cloneNode(true) : newHtml
        );

        for (const variantPicker of scopedHtml.querySelectorAll('variant-picker:not([data-picker-role="size"])')) {
          variantPicker.remove();
        }

        return originalUpdate(scopedHtml);
      };

      picker.dataset.vpcPatched = 'true';
    }
  }

  #patchModalVariantPickers() {
    for (const picker of this.refs.modalDetails?.querySelectorAll('variant-picker') ?? []) {
      if (picker.dataset.vpcCombinedPatched === 'true') continue;
      if (typeof picker.buildRequestUrl !== 'function') continue;

      const originalBuildRequestUrl = picker.buildRequestUrl.bind(picker);

      picker.buildRequestUrl = (selectedOption, source = null, sourceSelectedOptionsValues = []) => {
        if (source) {
          return originalBuildRequestUrl(selectedOption, source, sourceSelectedOptionsValues);
        }

        const combinedSelectedOptionsValues = [];

        for (const modalPicker of this.refs.modalDetails?.querySelectorAll('variant-picker') ?? []) {
          if (typeof modalPicker.selectedOptionsValues === 'undefined') continue;

          for (const optionValueId of modalPicker.selectedOptionsValues) {
            if (!combinedSelectedOptionsValues.includes(optionValueId)) {
              combinedSelectedOptionsValues.push(optionValueId);
            }
          }
        }

        Object.defineProperty(picker, 'selectedOptionsValues', {
          configurable: true,
          get() {
            return combinedSelectedOptionsValues;
          },
        });

        const requestUrl = originalBuildRequestUrl(selectedOption, source, sourceSelectedOptionsValues);

        delete picker.selectedOptionsValues;

        return requestUrl;
      };

      picker.dataset.vpcCombinedPatched = 'true';
    }
  }

  #buildMediaCarousel(images) {
    const carousel = document.createElement('div');
    carousel.className = 'vpc-modal-carousel';

    if (!images.length) {
      const placeholder = document.createElement('div');
      placeholder.className = 'vpc-modal-carousel__empty';
      carousel.appendChild(placeholder);
      return carousel;
    }

    const viewport = document.createElement('div');
    viewport.className = 'vpc-modal-carousel__viewport';

    const track = document.createElement('div');
    track.className = 'vpc-modal-carousel__track';

    for (const image of images) {
      const slide = document.createElement('div');
      slide.className = 'vpc-modal-carousel__slide';

      const clone = /** @type {HTMLImageElement} */ (image.cloneNode(false));
      clone.removeAttribute('loading');
      clone.removeAttribute('srcset');
      clone.removeAttribute('sizes');
      clone.className = 'vpc-modal-carousel__image';

      slide.appendChild(clone);
      track.appendChild(slide);
    }

    viewport.appendChild(track);
    carousel.appendChild(viewport);

    if (images.length === 1) {
      return carousel;
    }

    let currentIndex = 0;
    const maxIndex = images.length - 1;

    const updateSlides = () => {
      track.style.transform = `translateX(${-currentIndex * 100}%)`;
      previousButton.disabled = currentIndex <= 0;
      nextButton.disabled = currentIndex >= maxIndex;
    };

    const createArrow = (direction) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `vpc-modal-carousel__arrow vpc-modal-carousel__arrow--${direction > 0 ? 'next' : 'prev'}`;
      button.setAttribute('aria-label', direction > 0 ? 'Next image' : 'Previous image');
      button.innerHTML =
        direction > 0
          ? '<svg viewBox="0 0 8 14" fill="none" aria-hidden="true"><path d="M1 1L7 7L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
          : '<svg viewBox="0 0 8 14" fill="none" aria-hidden="true"><path d="M7 1L1 7L7 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      button.addEventListener('click', () => {
        currentIndex = Math.min(Math.max(0, currentIndex + direction), maxIndex);
        updateSlides();
      });
      return button;
    };

    const previousButton = createArrow(-1);
    const nextButton = createArrow(1);
    carousel.append(previousButton, nextButton);
    updateSlides();

    return carousel;
  }

  #initVideoPosters() {
    for (const video of this.querySelectorAll('.vpc-card__video')) {
      const poster = video.nextElementSibling;
      if (!poster?.classList.contains('vpc-card__video-poster')) continue;
      const hide = () => poster.classList.add('vpc-card__video-poster--hidden');
      if (video.readyState >= 3) {
        hide();
      } else {
        video.addEventListener('canplay', hide, { once: true });
      }
    }
  }

  /**
   * Moxie: carga diferida de videos. Los `<video>` se renderizan con
   * `preload="none"` y sin `autoplay`; aquí los reproducimos solo cuando entran
   * al viewport y los pausamos al salir. Así no se descargan los videos del
   * slider (bajo el fold) en el load inicial.
   */
  #initLazyVideos() {
    const videos = this.querySelectorAll('.vpc-card__video');
    if (!videos.length) return;

    if (!('IntersectionObserver' in window)) {
      for (const video of videos) video.play?.().catch(() => {});
      return;
    }

    this.#videoObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = /** @type {HTMLVideoElement} */ (entry.target);
          if (entry.isIntersecting) {
            if (video.preload === 'none') video.preload = 'auto';
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }
      },
      { rootMargin: '200px 0px' }
    );

    for (const video of videos) this.#videoObserver.observe(video);
  }
}

if (!customElements.get('custom-video-product-slider')) {
  customElements.define('custom-video-product-slider', VideoProductSlider);
}
